import { useEffect, useRef } from 'react';
import { noop } from 'lodash';

interface UseFocusTrapProps {
    isOpen: boolean;
    element: HTMLElement | null;
    onClose?: () => void;
}

interface UseFocusTrapReturnValue {
    close: () => void;
}

/**
 * This hook focuses on `element` in order to make sure the focus will be moved to the drawer when it's opened.
 * This is for accessibility, e.g. pressing on tab will not jump between elements in the background
 *
 * @param {UseFocusTrapProps} props
 * @param {boolean} props.isOpen Flag that indicates if the drawer is open
 * @param {HTMLElement | null} props.element The drawer's element to focus on
 * @param {(() => void) | undefined} props.onClose Function to invoke on close of the drawer
 * @return {() => void} Return method `close` to close the drawer
 */
export const useFocusTrap = ({ isOpen, element, onClose = noop }: UseFocusTrapProps): UseFocusTrapReturnValue => {
    const hasOpened = useRef(false);

    useEffect(() => {
        if (element && isOpen && !hasOpened.current) {
            hasOpened.current = true;
            element.focus();
        }
    }, [element, isOpen]);

    const close = () => {
        hasOpened.current = false;
        onClose();
    };

    return { close };
};
